import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { User, CreditCard, Bell, Shield, Download, CheckCircle, AlertTriangle, Loader, Settings as SettingsIcon, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Settings = ({ token, user, refreshUser }) => {
  const [activeSection, setActiveSection] = useState('profile');
  const [packages, setPackages] = useState({});
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [passwordChange, setPasswordChange] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [notificationPrefs, setNotificationPrefs] = useState({
    email_notifications: true,
    sms_notifications: false,
    weekly_reports: true,
    incident_alerts: true,
    maintenance_alerts: true
  });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [notificationSuccess, setNotificationSuccess] = useState('');
  const [testNotificationLoading, setTestNotificationLoading] = useState(false);

  useEffect(() => {
    fetchSubscriptionPackages();
    fetchNotificationPreferences();
    checkPaymentResult();
    checkUrlSection();
  }, []);

  const checkUrlSection = () => {
    // Check URL parameters for section to activate
    const urlParams = new URLSearchParams(window.location.search);
    const section = urlParams.get('section');
    
    if (section && ['profile', 'subscription', 'notifications', 'security', 'export'].includes(section)) {
      setActiveSection(section);
    }
  };

  const fetchSubscriptionPackages = async () => {
    try {
      const response = await axios.get(`${API}/subscription/packages`);
      setPackages(response.data);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    }
  };

  const fetchNotificationPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotificationPrefs(response.data);
    } catch (err) {
      console.error('Failed to fetch notification preferences:', err);
    }
  };

  const checkPaymentResult = () => {
    // Check if we're returning from payment gateway
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const subscriptionId = urlParams.get('subscription_id');
    const success = urlParams.get('success');
    const cancelled = urlParams.get('cancelled');
    const gateway = urlParams.get('gateway') || 'stripe';

    if ((sessionId || subscriptionId) && success) {
      setCheckingPayment(true);
      const identifier = sessionId || subscriptionId;
      pollPaymentStatus(identifier, gateway);
    } else if (cancelled) {
      setUpgradeError('Payment was cancelled. You can try again anytime.');
    }
  };

  const pollPaymentStatus = async (identifier, gateway, attempts = 0) => {
    const maxAttempts = 10;
    
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      setUpgradeError('Payment verification timed out. Please refresh the page.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/checkout/status/${identifier}?gateway=${gateway}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setCheckingPayment(false);
        // Refresh user data to update subscription tier
        if (refreshUser) {
          const updatedUser = await refreshUser();
          if (updatedUser && updatedUser.subscription_tier !== 'free') {
            // Show success message and clean URL
            window.history.replaceState({}, '', '/settings?upgraded=true');
            setUpgradeError('');
          }
        } else {
          // Fallback: refresh the page to update user data  
          window.location.href = window.location.origin + '/settings?upgraded=true';
        }
        return;
      } else if (response.data.gateway_status === 'expired' || response.data.gateway_status === 'cancelled') {
        setCheckingPayment(false);
        setUpgradeError('Payment session expired. Please try again.');
        return;
      }

      // Continue polling if payment is still pending
      setTimeout(() => pollPaymentStatus(identifier, gateway, attempts + 1), 2000);
    } catch (error) {
      setCheckingPayment(false);
      setUpgradeError('Error verifying payment. Please contact support.');
      console.error('Payment status error:', error);
    }
  };

  const handleUpgrade = async (packageId, paymentGateway = 'stripe', currency = 'usd') => {
    setUpgradeLoading(true);
    setUpgradeError('');

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout/session`, {
        package_id: packageId,
        payment_gateway: paymentGateway,
        currency: currency,
        origin_url: originUrl
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (paymentGateway === 'stripe') {
        // Redirect to Stripe Checkout
        if (response.data.url) {
          window.location.href = response.data.url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else if (paymentGateway === 'razorpay') {
        // Handle Razorpay subscription
        const subscription = response.data;
        
        // Open Razorpay checkout
        const options = {
          key: 'rzp_test_LgWaAuuKETb4wC', // You'll need to add this to frontend env
          subscription_id: subscription.subscription_id,
          name: 'URL Monitor Pro',
          description: `Subscription to ${packages[packageId]?.name}`,
          theme: {
            color: '#3B82F6'
          },
          handler: function(response) {
            // Handle successful payment
            console.log('Payment successful:', response);
            window.location.href = `${originUrl}/settings?subscription_id=${subscription.subscription_id}&success=true&gateway=razorpay`;
          },
          modal: {
            ondismiss: function() {
              setUpgradeLoading(false);
              setUpgradeError('Payment was cancelled');
            }
          }
        };
        
        const rzp = new window.Razorpay(options);
        rzp.open();
        setUpgradeLoading(false);
      }
    } catch (error) {
      setUpgradeLoading(false);
      setUpgradeError(error.response?.data?.detail || 'Failed to initiate payment');
      console.error('Upgrade error:', error);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Validate passwords
    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      setPasswordError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (passwordChange.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      setPasswordLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordChange.currentPassword,
        new_password: passwordChange.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswordSuccess('Password changed successfully!');
      setPasswordChange({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const confirmDelete = window.confirm(
        'Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.'
      );

      if (!confirmDelete) return;

      const doubleConfirm = window.prompt(
        'Type "DELETE" to confirm account deletion:'
      );

      if (doubleConfirm !== 'DELETE') {
        alert('Account deletion cancelled. You must type "DELETE" exactly.');
        return;
      }

      const response = await axios.delete(`${API}/auth/delete-account`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Account deleted successfully. You will be logged out.');
      // Clear local storage and redirect to login
      localStorage.removeItem('token');
      window.location.href = window.location.origin;
    } catch (error) {
      console.error('Delete account error:', error);
      alert(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  const handleExportData = async (exportType) => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/export/${exportType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Create and download file
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `url-monitor-pro-${exportType}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to export ${exportType} data`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleNotificationChange = async (setting, value) => {
    setNotificationLoading(true);
    try {
      const updateData = { [setting]: value };
      await axios.put(`${API}/notifications/preferences`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotificationPrefs(prev => ({ ...prev, [setting]: value }));
      setNotificationSuccess('Notification preferences updated successfully!');
      setTimeout(() => setNotificationSuccess(''), 3000);
    } catch (error) {
      console.error('Notification update error:', error);
      alert(error.response?.data?.detail || 'Failed to update notification preferences');
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleTestNotification = async (type) => {
    setTestNotificationLoading(true);
    try {
      const response = await axios.post(`${API}/notifications/test`, {
        notification_type: type,
        test_email: user.email  // Use the actual user's email address
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(`✅ Test ${type} notification sent successfully to ${user.email}!`);
    } catch (error) {
      console.error('Test notification error:', error);
      alert(error.response?.data?.detail || 'Failed to send test notification');
    } finally {
      setTestNotificationLoading(false);
    }
  };

  const handleDowngrade = async (targetTier) => {
    try {
      // Show detailed warning about billing cycle
      const user_tier = user?.subscription_tier;
      const billing_end = user?.subscription_ends_at;
      
      let warningMessage = `Are you sure you want to downgrade to ${targetTier} plan?\n\n`;
      
      if (billing_end) {
        const endDate = new Date(billing_end).toLocaleDateString();
        warningMessage += `Your current ${user_tier} plan will remain active until ${endDate}.\n`;
        warningMessage += `The downgrade will take effect at the end of your billing cycle.\n\n`;
      }
      
      if (targetTier === 'free') {
        if (user_tier === 'business') {
          warningMessage += `This will:\n• Disable up to 49 monitors (keeping only 1)\n• Reduce data retention to 30 days\n• Remove team features and API access`;
        } else if (user_tier === 'pro') {
          warningMessage += `This will:\n• Disable up to 9 monitors (keeping only 1)\n• Reduce data retention to 30 days\n• Remove premium features`;
        }
      } else if (targetTier === 'pro' && user_tier === 'business') {
        warningMessage += `This will:\n• Disable up to 40 monitors (keeping only 10)\n• Reduce data retention to 90 days\n• Remove team features and API access`;
      }
      
      warningMessage += `\n\nDisabled monitors will be preserved for 30 days and can be reactivated by upgrading.`;

      const confirmDowngrade = window.confirm(warningMessage);
      if (!confirmDowngrade) return;

      const response = await axios.post(`${API}/subscription/downgrade`, {
        target_tier: targetTier,
        reason: 'User requested downgrade',
        immediate: false  // Respect billing cycle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.immediate) {
        alert(`Downgraded to ${targetTier} plan immediately. ${response.data.impact?.monitors_disabled || 0} monitors were disabled.`);
      } else {
        const effectiveDate = new Date(response.data.effective_date).toLocaleDateString();
        alert(`Downgrade scheduled for ${effectiveDate}. Your current plan will remain active until then.`);
      }
      
      // Refresh user data
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Downgrade error:', error);
      alert(error.response?.data?.detail || 'Failed to process downgrade request');
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const reason = window.prompt('Please tell us why you\'re cancelling (optional):');
      
      const confirmCancel = window.confirm(
        'Are you sure you want to cancel your subscription? It will remain active until the end of your billing period.'
      );

      if (!confirmCancel) return;

      await axios.post(`${API}/subscription/cancel`, {
        reason: reason,
        immediate: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Subscription cancelled successfully. It will remain active until the end of your billing period.');
    } catch (error) {
      console.error('Cancel subscription error:', error);
      alert(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const sections = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'subscription', name: 'Subscription', icon: CreditCard },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'export', name: 'Export Data', icon: Download }
  ];

  const renderProfileSection = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Profile Information</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
          <input
            type="text"
            value={user?.name || ''}
            disabled
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm sm:text-base"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm sm:text-base break-all"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Member Since</label>
          <input
            type="text"
            value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : ''}
            disabled
            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm sm:text-base"
          />
        </div>
      </div>
    </div>
  );

  const renderSubscriptionSection = () => (
    <div className="space-y-6">
      {/* Success/Error Messages */}
      {checkingPayment && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg flex items-center">
          <Loader className="w-5 h-5 mr-2 animate-spin" />
          Verifying your payment... Please wait.
        </div>
      )}
      
      {upgradeError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
          <AlertTriangle className="w-5 h-5 mr-2" />
          {upgradeError}
        </div>
      )}

      {new URLSearchParams(window.location.search).get('upgraded') && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          Subscription upgraded successfully! Your new features are now active.
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">Current Plan</h3>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-4 sm:space-y-0">
          <div>
            <h4 className="text-lg sm:text-xl font-bold text-gray-900 capitalize">{user?.subscription_tier} Plan</h4>
            <p className="text-gray-600 text-sm sm:text-base">
              {user?.subscription_tier === 'free' ? 'Monitor 1 website' : 
               user?.subscription_tier === 'pro' ? 'Monitor up to 10 websites' :
               'Monitor up to 50 websites'}
            </p>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {user?.subscription_tier === 'free' ? '$0' : 
               user?.subscription_tier === 'pro' ? '$19' : '$49'}
            </p>
            <p className="text-gray-600 text-sm sm:text-base">per month</p>
            {refreshUser && (
              <button
                onClick={async () => {
                  setCheckingPayment(true);
                  await refreshUser();
                  setCheckingPayment(false);
                }}
                disabled={checkingPayment}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 underline"
              >
                {checkingPayment ? 'Refreshing...' : '🔄 Refresh Status'}
              </button>
            )}
          </div>
        </div>
        
        {user?.subscription_tier === 'free' && Object.keys(packages).length > 0 && (
          <div className="border-t border-gray-200 pt-4 sm:pt-6">
            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Upgrade Your Plan</h4>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
              {Object.entries(packages).map(([packageId, packageInfo]) => (
                <div key={packageId} className="border border-gray-200 rounded-lg p-4 sm:p-6">
                  <h5 className="font-semibold text-gray-900 text-base sm:text-lg mb-2">{packageInfo.name}</h5>
                  <p className="text-gray-600 text-sm mb-4">
                    {packageId === 'pro' ? 'Perfect for small businesses' : 'For growing companies'}
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4 sm:mb-6">
                    {packageInfo.features.map((feature, index) => (
                      <li key={index} className="break-words">• {feature}</li>
                    ))}
                  </ul>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">${packageInfo.price_usd}</p>
                        <p className="text-xs sm:text-sm text-gray-600">USD/month</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">₹{packageInfo.price_inr}</p>
                        <p className="text-xs sm:text-sm text-gray-600">INR/month</p>
                      </div>
                    </div>
                    
                    {/* International Payment - Stripe */}
                    <button 
                      onClick={() => handleUpgrade(packageId, 'stripe', 'usd')}
                      disabled={upgradeLoading || checkingPayment}
                      className="w-full py-2 sm:py-3 px-3 sm:px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-2 text-sm sm:text-base"
                    >
                      {upgradeLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </div>
                      ) : (
                        `Pay $${packageInfo.price_usd}/month (International)`
                      )}
                    </button>
                    
                    {/* Indian Payment - Razorpay with UPI */}
                    <button 
                      onClick={() => handleUpgrade(packageId, 'razorpay', 'inr')}
                      disabled={upgradeLoading || checkingPayment}
                      className="w-full py-2 sm:py-3 px-3 sm:px-4 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                    >
                      {upgradeLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </div>
                      ) : (
                        `₹${packageInfo.price_inr}/month (UPI/Cards/NetBanking)`
                      )}
                    </button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      🇮🇳 UPI/eMandate for recurring payments | 🌍 Cards for international
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {user?.subscription_tier === 'pro' && Object.keys(packages).length > 0 && (
          <div className="border-t border-gray-200 pt-4 sm:pt-6">
            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Upgrade to Business Plan</h4>
            <p className="text-gray-600 text-sm mb-4">
              Scale up your monitoring with our Business plan for more websites and advanced features.
            </p>
            <div className="max-w-md">
              {packages.business && (
                <div className="border border-gray-200 rounded-lg p-4 sm:p-6">
                  <h5 className="font-semibold text-gray-900 text-base sm:text-lg mb-2">{packages.business.name}</h5>
                  <p className="text-gray-600 text-sm mb-4">For growing companies</p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4 sm:mb-6">
                    {packages.business.features.map((feature, index) => (
                      <li key={index} className="break-words">• {feature}</li>
                    ))}
                  </ul>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">${packages.business.price_usd}</p>
                        <p className="text-xs sm:text-sm text-gray-600">USD/month</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xl sm:text-2xl font-bold text-gray-900">₹{packages.business.price_inr}</p>
                        <p className="text-xs sm:text-sm text-gray-600">INR/month</p>
                      </div>
                    </div>
                    
                    {/* International Payment - Stripe */}
                    <button 
                      onClick={() => handleUpgrade('business', 'stripe', 'usd')}
                      disabled={upgradeLoading || checkingPayment}
                      className="w-full py-2 sm:py-3 px-3 sm:px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors mb-2 text-sm sm:text-base"
                    >
                      {upgradeLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </div>
                      ) : (
                        `Upgrade to $${packages.business.price_usd}/month (International)`
                      )}
                    </button>
                    
                    {/* Indian Payment - Razorpay with UPI */}
                    <button 
                      onClick={() => handleUpgrade('business', 'razorpay', 'inr')}
                      disabled={upgradeLoading || checkingPayment}
                      className="w-full py-2 sm:py-3 px-3 sm:px-4 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
                    >
                      {upgradeLoading ? (
                        <div className="flex items-center justify-center">
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </div>
                      ) : (
                        `Upgrade to ₹${packages.business.price_inr}/month (UPI/Cards/NetBanking)`
                      )}
                    </button>
                    
                    <p className="text-xs text-gray-500 text-center">
                      🇮🇳 UPI/eMandate for recurring payments | 🌍 Cards for international
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {user?.subscription_tier === 'business' && (
          <div className="border-t border-gray-200 pt-6">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              You're on our highest tier! You have access to all premium features including 50 websites, team features, and API access.
            </div>
          </div>
        )}

        {user?.subscription_tier !== 'free' && (
          <div className="border-t border-gray-200 pt-4 sm:pt-6 mt-6">
            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Subscription Management</h4>
            
            <div className="space-y-4">
              {/* Downgrade Options */}
              {user?.subscription_tier === 'business' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h5 className="font-medium text-yellow-800 mb-2">Downgrade to Pro Plan</h5>
                  <p className="text-sm text-yellow-700 mb-3">
                    Reduce your monitor limit to 10 websites and save on costs.
                  </p>
                  <button
                    onClick={() => handleDowngrade('pro')}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors text-sm"
                  >
                    Downgrade to Pro ($19/month)
                  </button>
                </div>
              )}
              
              {(user?.subscription_tier === 'pro' || user?.subscription_tier === 'business') && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h5 className="font-medium text-red-800 mb-2">Downgrade to Free Plan</h5>
                  <p className="text-sm text-red-700 mb-3">
                    <strong>Warning:</strong> You'll lose access to paid features and excess monitors will be disabled.
                    {user?.subscription_tier === 'business' && ' Up to 49 monitors will be disabled.'}
                    {user?.subscription_tier === 'pro' && ' Up to 9 monitors will be disabled.'}
                  </p>
                  <div className="text-xs text-red-600 mb-3">
                    • Monitor data will be preserved for 30 days<br/>
                    • Incidents older than 30 days will be archived<br/>
                    • You can upgrade again anytime to restore access
                  </div>
                  <button
                    onClick={() => handleDowngrade('free')}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Downgrade to Free
                  </button>
                </div>
              )}
              
              {/* Cancel Subscription */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 mb-2">Cancel Subscription</h5>
                <p className="text-sm text-gray-700 mb-3">
                  Cancel your subscription and stop recurring billing. Your plan will remain active until the end of the billing period.
                </p>
                <button
                  onClick={() => handleCancelSubscription()}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderNotificationsSection = () => (
    <div className="space-y-6">
      {notificationSuccess && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {notificationSuccess}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 sm:mb-6 space-y-4 sm:space-y-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900">Notification Preferences</h3>
          <button
            onClick={() => handleTestNotification('email')}
            disabled={testNotificationLoading}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
          >
            {testNotificationLoading ? (
              <div className="flex items-center">
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Testing...
              </div>
            ) : (
              '🧪 Send Test Email'
            )}
          </button>
        </div>
        
        <div className="space-y-4 sm:space-y-6">
          {/* Email Notifications */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 bg-gray-50 rounded-lg space-y-3 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <h4 className="font-medium text-gray-900 text-sm sm:text-base">Email Notifications</h4>
                <p className="text-xs sm:text-sm text-gray-600">Receive alerts and updates via email</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 justify-end">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationPrefs.email_notifications}
                  onChange={(e) => handleNotificationChange('email_notifications', e.target.checked)}
                  disabled={notificationLoading}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationPrefs.email_notifications ? 'bg-blue-600' : 'bg-gray-300'
                } ${notificationLoading ? 'opacity-50' : ''}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationPrefs.email_notifications ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>

          {/* Incident Alerts */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Incident Alerts</h4>
                <p className="text-sm text-gray-600">Get notified when monitors go down</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleTestNotification('incident')}
                disabled={testNotificationLoading}
                className="text-sm bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 disabled:opacity-50 transition-colors"
              >
                Test Alert
              </button>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationPrefs.incident_alerts}
                  onChange={(e) => handleNotificationChange('incident_alerts', e.target.checked)}
                  disabled={notificationLoading}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationPrefs.incident_alerts ? 'bg-red-600' : 'bg-gray-300'
                } ${notificationLoading ? 'opacity-50' : ''}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationPrefs.incident_alerts ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>

          {/* SMS Notifications */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg opacity-75">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Bell className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">SMS Notifications</h4>
                <p className="text-sm text-gray-600">Receive alerts via SMS (Pro plan required)</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                {user?.subscription_tier === 'free' ? 'Pro Required' : 'Available'}
              </span>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationPrefs.sms_notifications}
                  onChange={(e) => handleNotificationChange('sms_notifications', e.target.checked)}
                  disabled={notificationLoading || user?.subscription_tier === 'free'}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationPrefs.sms_notifications && user?.subscription_tier !== 'free' ? 'bg-green-600' : 'bg-gray-300'
                } ${notificationLoading ? 'opacity-50' : ''}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationPrefs.sms_notifications && user?.subscription_tier !== 'free' ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>

          {/* Weekly Reports */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Weekly Reports</h4>
                <p className="text-sm text-gray-600">Receive weekly uptime summaries</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleTestNotification('weekly_report')}
                disabled={testNotificationLoading}
                className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded hover:bg-purple-200 disabled:opacity-50 transition-colors"
              >
                Test Report
              </button>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationPrefs.weekly_reports}
                  onChange={(e) => handleNotificationChange('weekly_reports', e.target.checked)}
                  disabled={notificationLoading}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationPrefs.weekly_reports ? 'bg-purple-600' : 'bg-gray-300'
                } ${notificationLoading ? 'opacity-50' : ''}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationPrefs.weekly_reports ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>

          {/* Maintenance Alerts */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <SettingsIcon className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Maintenance Alerts</h4>
                <p className="text-sm text-gray-600">Notifications about service maintenance</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={notificationPrefs.maintenance_alerts}
                  onChange={(e) => handleNotificationChange('maintenance_alerts', e.target.checked)}
                  disabled={notificationLoading}
                  className="sr-only"
                />
                <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationPrefs.maintenance_alerts ? 'bg-orange-600' : 'bg-gray-300'
                } ${notificationLoading ? 'opacity-50' : ''}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationPrefs.maintenance_alerts ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Test Email Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">🧪 Test Notifications</h3>
        <p className="text-gray-600 mb-4 text-sm sm:text-base">Send test notifications to verify your settings</p>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <button
            onClick={() => handleTestNotification('email')}
            disabled={testNotificationLoading}
            className="flex items-center justify-center space-x-2 p-3 sm:p-4 border-2 border-blue-200 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
          >
            <User className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span>Test Email</span>
          </button>
          
          <button
            onClick={() => handleTestNotification('incident')}
            disabled={testNotificationLoading}
            className="flex items-center justify-center space-x-2 p-3 sm:p-4 border-2 border-red-200 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
          >
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span>Test Alert</span>
          </button>
          
          <button
            onClick={() => handleTestNotification('weekly_report')}
            disabled={testNotificationLoading}
            className="flex items-center justify-center space-x-2 p-3 sm:p-4 border-2 border-purple-200 text-purple-700 rounded-lg hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
          >
            <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
            <span>Test Report</span>
          </button>
        </div>
        
        <div className="mt-4 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs sm:text-sm text-blue-700">
            <strong>📧 Test Email:</strong> {user?.email}<br/>
            All test notifications will be sent to your registered email address.
          </p>
        </div>
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Security Settings</h3>
      <div className="space-y-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-4">Change Password</h4>
          <p className="text-sm text-gray-600 mb-4">Update your password to keep your account secure</p>
          
          {passwordSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              {passwordSuccess}
            </div>
          )}
          
          {passwordError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {passwordError}
            </div>
          )}
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
              <input
                type="password"
                value={passwordChange.currentPassword}
                onChange={(e) => setPasswordChange(prev => ({...prev, currentPassword: e.target.value}))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="Enter current password"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
              <input
                type="password"
                value={passwordChange.newPassword}
                onChange={(e) => setPasswordChange(prev => ({...prev, newPassword: e.target.value}))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="Enter new password"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
              <input
                type="password"
                value={passwordChange.confirmPassword}
                onChange={(e) => setPasswordChange(prev => ({...prev, confirmPassword: e.target.value}))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                placeholder="Confirm new password"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={passwordLoading}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {passwordLoading ? (
                <div className="flex items-center">
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Changing Password...
                </div>
              ) : (
                'Change Password'
              )}
            </button>
          </form>
        </div>
        
        <div className="border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-900 mb-2">Two-Factor Authentication</h4>
          <p className="text-sm text-gray-600 mb-4">Add an extra layer of security to your account</p>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Enable 2FA (Coming Soon)
          </button>
        </div>
        
        <div className="border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-900 mb-2 text-red-700">Danger Zone</h4>
          <p className="text-sm text-gray-600 mb-4">Permanently delete your account and all data</p>
          <button 
            onClick={handleDeleteAccount}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>
  );

  const renderExportSection = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Export Your Data</h3>
      <div className="space-y-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Monitor Data</h4>
          <p className="text-sm text-gray-600 mb-4">Download all your monitor configurations and check history</p>
          <button 
            onClick={() => handleExportData('monitors')}
            disabled={exportLoading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exportLoading ? (
              <div className="flex items-center">
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </div>
            ) : (
              <div className="flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Download Monitor Data
              </div>
            )}
          </button>
        </div>
        
        <div className="border-t border-gray-200 pt-6">
          <h4 className="font-medium text-gray-900 mb-2">Incident Reports</h4>
          <p className="text-sm text-gray-600 mb-4">Export all incident reports and downtime history</p>
          <button 
            onClick={() => handleExportData('incidents')}
            disabled={exportLoading}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exportLoading ? (
              <div className="flex items-center">
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Exporting...
              </div>
            ) : (
              <div className="flex items-center">
                <Download className="w-4 h-4 mr-2" />
                Download Incident Reports
              </div>
            )}
          </button>
        </div>
        
        <div className="border-t border-gray-200 pt-6">
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg">
            <h5 className="font-medium mb-1">Data Export Information</h5>
            <ul className="text-sm space-y-1">
              <li>• Data is exported in JSON format</li>
              <li>• Monitor data includes recent 100 checks per monitor</li>
              <li>• All timestamps are in UTC format</li>
              <li>• Files are named with current date for easy organization</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection();
      case 'subscription':
        return renderSubscriptionSection();
      case 'notifications':
        return renderNotificationsSection();
      case 'security':
        return renderSecuritySection();
      case 'export':
        return renderExportSection();
      default:
        return renderProfileSection();
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 xl:p-10">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Settings</h2>
        <p className="text-gray-600 text-sm sm:text-base">Manage your account and preferences</p>
      </div>

      <div className="flex flex-col lg:flex-row lg:space-x-8 space-y-6 lg:space-y-0">
        {/* Mobile Section Selector */}
        <div className="lg:hidden">
          <select
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
          >
            {sections.map((section) => (
              <option key={section.id} value={section.id}>
                {section.name}
              </option>
            ))}
          </select>
        </div>

        {/* Desktop Sidebar */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <nav className="space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors ${
                  activeSection === section.id
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <section.icon className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">{section.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;